import React, { useEffect, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleBadge,
  BubbleDebounce,
} from '@go-client/components/BubbleUI';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import ReservationPath, {
  ReservationFormMode,
} from '@go-client/components/ReservationPath/ReservationPath';
import { Reservation } from '@go-shared/types/reservation';
import { SortOrder } from '@go-shared/types/userActionRecord';
import {
  DEFAULT_PER_PAGE,
  PaginationMetadata,
  PaginationResult,
} from '@go-shared/types/pagination';
import { readQs } from '@go-shared/lib/querystring';
import {
  get as getReservation,
  toggleStatus,
  paginateReservations,
  exportTransactionsToCsv,
} from '@go-client/lib/api/reservation';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import { hasOwnProperty, makeFullName, formatCentsToUSD } from '@go-shared/lib/utils';
import { calculateReservationAnalytics, isFlattenedReservation } from '@go-shared/lib/reserve';
import { ReservationTableFields, ReservationTableItem } from './ReserveOngoing';
import {
  BubbleTableFieldBehavior,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { User } from '@go-shared/types/user';
import { getUsers } from '@go-client/lib/api/user';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import {
  addMinutesToTime,
  dateToIsoUTC,
  DEFAULT_TIMESTR_FORMAT,
  getLocaleDateTimeStringLocal,
  timeToString,
} from '@go-shared/lib/time';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { getHourFormat } from '@go-shared/lib/property';
import qslib from '@go-shared/lib/querystring';
import phone from 'phone';
import { Alert, Anchor } from '@spark-labs/avocado-ui';
import { mapBy } from '@go-shared/lib/functional';
import { HourFormat } from '@go-shared/types/brand';
import {
  canCreateSomeReservations,
  canHostEvents,
  canManageAllReservations,
  canManageReservation,
} from '@go-shared/lib/permissions';

interface ReserveTransactionsParams {
  property: string;
}

type ReserveTransactionsProps = RouteComponentProps<ReserveTransactionsParams>;

type PaginationParams<T> = {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
  hostId?: string;
};

interface ReserveTransactionsQueryStrings {
  reservation?: string;
}

function getQueryStrings(): ReserveTransactionsQueryStrings {
  const qs = qslib.readQs();

  const reservation =
    hasOwnProperty(qs, 'reservation') && typeof qs.reservation === 'string'
      ? qs.reservation
      : undefined;

  return { reservation };
}

export function makeReservationTableItem<T extends Reservation>(
  res: T,
  usersDict: Record<string, User>,
  hourFormat: HourFormat,
  indirectReservationsCheck?: (reservation: T) => boolean
): ReservationTableItem<T> {
  const item: ReservationTableItem<T> = {
    id: res._id,
    event: res.eventSnapshot.title,
    host:
      res.host && usersDict[res.host]
        ? makeFullName(usersDict[res.host].firstName, usersDict[res.host].lastName)
        : undefined,
    resource: res.resource
      ? res.eventSnapshot.reservables.tickets.find(r => r._id === res.resource)?.name
      : undefined,
    date: dateToIsoUTC(res.date),
    startTime: timeToString(res.time, hourFormat, DEFAULT_TIMESTR_FORMAT),
    endTime: res.duration
      ? timeToString(addMinutesToTime(res.time, res.duration), hourFormat, DEFAULT_TIMESTR_FORMAT)
      : undefined,
    status: res.status,
    statusVariant: (res.status === 'cancelled' ? 'crimson' : 'lime') as BubbleBadgeProps['variant'],
    attendance: res.attendance,
    attendanceVariant: (res.attendance === 'no-show'
      ? 'crimson'
      : res.attendance === 'attended'
        ? 'lime'
        : 'smoke') as BubbleBadgeProps['variant'],
    cartCode: res.cartCode || '',
    reservationCode: res.reservationCode,
    name: makeFullName(res.customer.firstName, res.customer.lastName),
    email: res.customer.email,
    phone: phone(res.customer.phone).phoneNumber || res.customer.phone,
    isIndirect: indirectReservationsCheck ? indirectReservationsCheck(res) : false,
    isCancelled: res.status === 'cancelled',
    isOccurrence: isFlattenedReservation(res) ? !!res._isOccurrenceOf : false,
    sourceReservationId: isFlattenedReservation(res) ? res._isOccurrenceOf || res._id : res._id,
    analytics: calculateReservationAnalytics([res]),
    _reservation: res,
    _event: res.eventSnapshot,
  };

  return item;
}

export function makeReservationTableFields(): ReservationTableFields[] {
  return [
    { key: 'status', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'reservationCode', label: 'Reservation' },
    { key: 'time' },
    { key: 'host', label: 'Rental / Host' },
    { key: 'name' },
    { key: 'email' },
    { key: 'phone' },
    { key: 'revenue' },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];
}

export function makeReservationTableTemplates(): BubbleTableTemplate<ReservationTableItem>[] {
  return [
    {
      key: 'status',
      render: ({ statusVariant, status, isOccurrence, isIndirect }) => (
        <div>
          <BubbleBadge className="mt-1 mr-1" variant={statusVariant}>
            {status}
          </BubbleBadge>
          {isOccurrence && (
            <BubbleBadge variant="ocean" className="mt-1 mr-1">
              Occurrence
            </BubbleBadge>
          )}
          {isIndirect && (
            <BubbleBadge variant="banana" className="mt-1 mr-1">
              Indirect
            </BubbleBadge>
          )}
        </div>
      ),
    },
    {
      key: 'reservationCode',
      render: ({ reservationCode, cartCode }) => (
        <div>
          <div>{reservationCode}</div>
          {cartCode && <div className="text-xs text-slate">{cartCode}</div>}
        </div>
      ),
    },
    {
      key: 'time',
      render: ({ startTime, endTime }) => (
        <div>
          {startTime}
          <span className="whitespace-nowrap">{endTime ? ` - ${endTime}` : ''}</span>
        </div>
      ),
    },
    {
      key: 'host',
      render: ({ host, resource }) =>
        (host || resource) && (
          <div>
            {resource && <div>{resource}</div>}
            <div className={`${resource ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'name',
    },
    {
      key: 'email',
    },
    {
      key: 'phone',
    },
    {
      key: 'revenue',
      render: data => (
        <span
          className={`${data.analytics.totalRevenue === 0 ? '' : data.analytics.totalRevenue > 0 ? 'font-bold text-lime-darkest' : 'font-bold text-crimson'}`}
        >
          {formatCentsToUSD(data.analytics.totalRevenue)}
        </span>
      ),
    },
  ];
}

export default function ReserveTransactions(props: ReserveTransactionsProps) {
  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();

  const hourFormat = getHourFormat(property);

  const [loading, setLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [mode, _setMode] = useState<ReservationFormMode | undefined>(undefined);
  const [transactions, setTransactions] = useState<PaginationResult<Reservation> | undefined>(
    undefined
  );
  const [users, setUsers] = useState<User[]>([]);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const queries = readQs();

  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );
  const [paginationParams, setPaginationParams] = useState<PaginationParams<Reservation>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    archived: false,
    propertyId: property._id,
    search: typeof queries.search === 'string' ? queries.search : '',
    hostId: canHostEvents(user) ? user._id : undefined,
  });

  async function fetchData() {
    setLoading(true);

    try {
      const [users, transactions] = await Promise.all([
        getUsers(property._id),
        paginateReservations({
          page: paginationParams.page,
          perPage: paginationParams.perPage,
          sortBy: paginationParams.sortBy,
          sortDirection: paginationParams.sortDirection,
          propertyId: property._id,
          ...(paginationParams.search && { search: paginationParams.search }),
          ...(paginationParams.archived && { archived: paginationParams.archived }),
          ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
          ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
          ...(paginationParams.hostId && { hostId: paginationParams.hostId }),
        }),
      ]);

      setPaginationMetadata(transactions.meta);

      setUsers(users.filter(canHostEvents));
      setTransactions(transactions);
      refreshSelectedReservation().catch(console.error);
    } finally {
      setLoading(false);
    }
  }

  async function handleExportCSV() {
    setIsBusy(true);

    try {
      let startDate;
      let endDate;
      let includeCancelled = true;

      const shouldContinue = await Alert.question({
        htmlContent: (
          <div className="p-3 w-full">
            <div className="grid grid-cols-1 gap-4">
              <BubbleFormGroup label="Start Date">
                <BubbleInput
                  block
                  type="date"
                  value={startDate}
                  onValue={value => (startDate = value)}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="End Date">
                <BubbleInput
                  block
                  type="date"
                  value={endDate}
                  onValue={value => (endDate = value)}
                />
              </BubbleFormGroup>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="bubble-input"
                  onChange={evt => (includeCancelled = !evt.target.checked)}
                />
                <span className="ml-2 text-sm">Omit Cancelled</span>
              </div>
            </div>
          </div>
        ),
        title: 'Export CSV',
        yesButton: 'Export',
        noButton: 'Cancel',
      });

      if (!shouldContinue) {
        return;
      }

      const { location } = await exportTransactionsToCsv(
        property._id,
        startDate,
        endDate,
        includeCancelled
      );

      return Alert.success({
        htmlContent: (
          <div className="pt-2 pb-4">
            <Anchor target="_blank" href={location}>
              Download CSV
            </Anchor>
          </div>
        ),
      });
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, propertyId: property._id });
    }
  }, [property]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [paginationParams, property._id]);

  function handleOpenReservationPanel(newMode: ReservationFormMode) {
    _setMode(newMode);
    if (newMode.type === 'edit') {
      qslib.writeQs({ reservation: newMode.edit._id });
    }
  }

  function handleCloseReservationPanel() {
    _setMode(undefined);
    qslib.writeQs({});
  }

  function handleSubmitReservation(newReservation: Reservation) {
    fetchData().catch(console.error);
    handleOpenReservationPanel({
      type: 'edit',
      edit: newReservation,
    });
  }

  async function handleToggleReservation(reservation: Reservation) {
    await toggleStatus(reservation);
    setShowDelete({ ...showDelete, [reservation._id]: false });
    fetchData().catch(console.error);
  }

  async function refreshSelectedReservation() {
    const parsedQs = getQueryStrings();

    const toSelect = parsedQs.reservation;
    if (!toSelect) return;

    const reservation = await getReservation(property._id, toSelect);

    handleOpenReservationPanel({ type: 'edit', edit: reservation });
  }

  const usersDict = mapBy(users, '_id');
  const tableItems: ReservationTableItem[] = (transactions?.data ?? []).map(res =>
    makeReservationTableItem(res, usersDict, hourFormat)
  );

  const tableFields = makeReservationTableFields();

  tableFields.splice(1, 0, {
    key: 'event',
    label: 'Experience',
  });
  tableFields.splice(3, 0, {
    key: 'date',
  });
  tableFields.splice(10, 0, {
    key: 'createdAt',
    behavior: BubbleTableFieldBehavior.NoWrap,
  });

  const tableTemplates: BubbleTableTemplate<ReservationTableItem>[] = [
    ...makeReservationTableTemplates(),
    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._reservation.createdAt) {
          const createdAt = getLocaleDateTimeStringLocal(new Date(item._reservation.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: data => (
        <>
          <BubbleButton
            onClick={() => handleOpenReservationPanel({ type: 'edit', edit: data._reservation })}
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
          />
          {canManageReservation(user, data._reservation) && (
            <>
              {!showDelete[data.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [data.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[data.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="dirt"
                    inverse
                    onClick={() => handleToggleReservation(data._reservation)}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [data.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={property} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={property.brand.logo} />
            <PropertyBanner {...props} property={property} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <fieldset disabled={isBusy} className="grid gap-2">
              {canCreateSomeReservations(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  icon="plus"
                  onClick={() => {
                    handleOpenReservationPanel({
                      type: 'create',
                    });
                  }}
                >
                  Add Reservation
                </BubbleButton>
              )}
              <BubbleButton
                variant="slate"
                className="text-left"
                icon="file-export"
                onClick={handleExportCSV}
              >
                Export to CSV
              </BubbleButton>
              <BubbleButton
                variant="white"
                inverse
                className="text-left border border-white"
                icon="repeat"
                onClick={() => fetchData().catch(console.error)}
              >
                Refresh Data
              </BubbleButton>
            </fieldset>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleHintSelect
                    block
                    variant="turquoise"
                    allowUndefined
                    placeholder="Filter host shown..."
                    options={users.map(u => u._id)}
                    labelExtractor={u =>
                      makeFullName(usersDict[u]?.firstName, usersDict[u]?.lastName) ||
                      'Unknown User'
                    }
                    value={paginationParams.hostId}
                    onValue={hostId =>
                      setPaginationParams({ ...paginationParams, page: 1, hostId })
                    }
                  />
                </BubbleFormGroup>
              )}
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="reservations" property={property} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div style={{ marginTop: 'calc(40px + 0.75rem)' }}>
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  stickyHeader={182}
                  onRowClick={subItem =>
                    handleOpenReservationPanel({ type: 'edit', edit: subItem._reservation })
                  }
                  headVariant="turquoise"
                  fields={tableFields}
                  templates={tableTemplates}
                  items={tableItems}
                  keyExtractor={item => item.id}
                  showPretitle
                  pretitleClassName="p-0-i"
                  pretitleRender={() => (
                    <div className="px-4 pt-4 pb-1">
                      <BubbleDebounce
                        onTrigger={[
                          (search: string) =>
                            setPaginationParams({ ...paginationParams, page: 1, search }),
                          500,
                        ]}
                      >
                        {({ debounce }) => (
                          <BubbleInput
                            type="text"
                            variant="turquoise"
                            block
                            value={paginationParams.search}
                            inputClassName="outline-none-i"
                            onValue={debounce}
                            placeholder="Search by event, customer name, email, or reservation code..."
                          />
                        )}
                      </BubbleDebounce>
                    </div>
                  )}
                />
                {paginationMetadata && (
                  <BubbleTablePaginationFooter
                    paginationMetadata={paginationMetadata}
                    perPage={paginationParams.perPage}
                    onLoadMoreClick={() =>
                      setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
                    }
                    onPerPageChange={value =>
                      setPaginationParams({ ...paginationParams, page: 1, perPage: value })
                    }
                  />
                )}
              </BubbleOverlay>
            </div>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseReservationPanel}>
            <ReservationPath
              mode={mode}
              users={users}
              onClose={handleCloseReservationPanel}
              onRefreshReservation={handleSubmitReservation}
              onNavigateToReservation={r => handleOpenReservationPanel({ type: 'edit', edit: r })}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
}
